import { docgenEnum } from '@polestar/component-warehouse-react/helpers/docgenInterface';
import { cssCustomPropPrefix } from '@polestar/component-warehouse-react/helpers/style';
import { MotionEasings, createCSSTransition, cubicBezier, createCustomMotionDuration, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { createCustomSpacing } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
export const primaryNavigationId = 'primary-navigation';
export const secondaryNavigationId = 'secondary-navigation';
// The duration (in milliseconds) it takes before the overlay triggers the menu to close on hover
export const overlayHoverGraceTime = createCustomMotionDuration(300);
export var MegaMenuMotionDurations;
(function (MegaMenuMotionDurations) {
    MegaMenuMotionDurations[MegaMenuMotionDurations["d650"] = 650] = "d650";
    MegaMenuMotionDurations[MegaMenuMotionDurations["d450"] = 450] = "d450";
    MegaMenuMotionDurations[MegaMenuMotionDurations["fast"] = 200] = "fast";
    MegaMenuMotionDurations[MegaMenuMotionDurations["d150"] = 150] = "d150";
})(MegaMenuMotionDurations || (MegaMenuMotionDurations = {}));
export const headerDisappearingMotionOptions = {
    duration: createCustomMotionDuration(0.35),
    ease: 'easeInOut',
};
// the duration (in milliseconds) to avoid accidental hovers/mouse enters of items in the main navigation
export const hoverGracePeriod = MegaMenuMotionDurations.d150;
export const fadeTransitions = `${MegaMenuMotionDurations.fast}ms ${cubicBezier(MotionEasings.linear)}`;
export const flyoutPanelTransitionDuration = MegaMenuMotionDurations.d650;
export const flyoutPanelTransition = createCSSTransition('translate', cubicBezier([0.43, 0.01, 0.27, 0.99]), flyoutPanelTransitionDuration);
export const flyoutDesktopClipPathTransition = createCSSTransition('clip-path', cubicBezier([0.43, 0, 0.42, 1]), MegaMenuMotionDurations.d450);
export const linkHoverTransition = createCSSTransition('opacity', MotionEasings.easeInOut, MegaMenuMotionDurations.d150);
export const tabletInlineSize = '50%';
/**
 * @internal
 */
export const megaMenuZIndexes = {
    navigationContainer: 1,
    flyout: 2,
    sharedCloseButton: 3,
};
/**
 * TODO: This needs to be globalised and needs TLC from the team how to use this in the future
 */
export const cwRTLTranslate = `${cssCustomPropPrefix}-rtl-translate`;
const cssCustomPropMegaMenuPrefix = `${cssCustomPropPrefix}-mega-menu`;
/**
 * @internal
 */
export const cssCustomPropInternalMegaMenuPrefix = `${cssCustomPropPrefix}-internal-mega-menu`;
export var MegaMenuCSSCustomProperties;
(function (MegaMenuCSSCustomProperties) {
    /**
     * @internal do not use
     */
    MegaMenuCSSCustomProperties["isLightTheme"] = "--cw-internal-mega-menu-is-light-theme";
    /**
     * The height of the MegaMenu bar (with the sub-navigation slot).
     *
     * Please use `barHeight` and `subNavigationHeight` if any of these parts need a different value so it is clear what kind of intention a code-change has.
     */
    MegaMenuCSSCustomProperties["blockSize"] = "--cw-mega-menu-block-size";
    /**
     * The height of the MegaMenu bar only (without the sub-navigation slot)
     */
    MegaMenuCSSCustomProperties["barHeight"] = "--cw-mega-menu-bar-height";
    /**
     * The height of the sub-navigation slot
     */
    MegaMenuCSSCustomProperties["subNavigationHeight"] = "--cw-mega-menu-sub-nav-height";
    /**
     * The amount of space until the MegaMenu bar from the top of the window
     */
    MegaMenuCSSCustomProperties["menuTopOffset"] = "--cw-mega-menu-top-offset";
    /**
     * @internal do not use
     */
    MegaMenuCSSCustomProperties["background"] = "--cw-internal-mega-menu-background";
    /**
     * @internal do not use
     */
    MegaMenuCSSCustomProperties["color"] = "--cw-internal-mega-menu-color";
    /**
     * @internal do not use
     */
    MegaMenuCSSCustomProperties["divider"] = "--cw-internal-mega-menu-divider";
    /**
     * @internal do not use
     */
    MegaMenuCSSCustomProperties["tabletMaxInlineSize"] = "--cw-internal-mega-menu-tablet-max-inline-size";
    /**
     * Provides a state (1|0) when the header is hidden (1) or not (0)
     */
    MegaMenuCSSCustomProperties["hideHeader"] = "--cw-mega-menu-hide-header";
    /**
     * Provide the current height of the visible area of the MegaMenu (without the sub-navigation slot height)
     */
    MegaMenuCSSCustomProperties["safeAreaHeight"] = "--cw-mega-menu-safe-area-height";
    /**
     * The maximum height of the MegaMenu (including sub navigation). This value will not update on scroll, but will update when e.g. individual items within the MegaMenu increase in height (like after font-size increase or layout updates on viewport resizing).
     * This value is ideal for Heroes that need to calculate the remaining height available and could look something like this:
     *
     * `block-size: calc(100svh - ${cssVar(MegaMenuCSSCustomProperties.maxSafeAreaHeight, '')})`;
     */
    MegaMenuCSSCustomProperties["maxSafeAreaHeight"] = "--cw-mega-menu-max-safe-area-height";
    /**
     * Showing the animated progress of the disappearing header between 0 and 1. This value is used to animate the header out and into view based on scroll.
     */
    MegaMenuCSSCustomProperties["hideHeaderProgress"] = "--cw-mega-menu-hide-header-progress";
    /**
     * @internal do not use
     */
    MegaMenuCSSCustomProperties["variant"] = "--cw-internal-mega-menu-variant";
})(MegaMenuCSSCustomProperties || (MegaMenuCSSCustomProperties = {}));
export const MegaMenuCSSCustomPropertiesInterface = docgenEnum('MegaMenuCSSCustomProperties', MegaMenuCSSCustomProperties);
/**
 * This is needed for SSR to ensure a minimal / initial value to render from until JS is loaded fully.
 *
 * @internal
 */
export const megaMenuMinimalSafeAreaHeight = createCustomSpacing(56 / 16);
/**
 * @internal
 */
export const flyoutDesktopBlockSize = `min(${createCustomSpacing(424 / 16)}, 90vh)`;
/**
 * @internal
 * Pixel value for the height of the mask area
 */
export const megaMenuScrollHintSize = 80;
/**
 * @internal
 * Pixel value to trigger the visibility of the top or bottom gradient effect
 */
export const megaMenuScrollHintTriggerSize = 40;
/**
 * @internal
 * The amount of time to wait extra to ensure that the animation is not triggered after the useBreakpointChanged hook
 */
export const megaMenuBreakpointCheckDelay = 50;
